import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import React, { useRef, useState } from 'react';

import { EditableSelectOption, EditableSelectProps } from './EditableSelect.props';

export const EditableSelect: React.FC<EditableSelectProps> = ({
  value,
  displayValue,
  isEditable,
  ariaLabel,
  options,
  placeholder = 'Select...',
  onSave,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<EditableSelectOption | null>(null);

  const handleOpen = () => {
    setDraft(options.find((o) => o.value === value) ?? null);
    setOpen(true);
  };

  const handleSave = () => {
    if (draft) onSave(draft.value);
    setOpen(false);
  };

  const handleCancel = () => setOpen(false);

  const resolvedDisplay = (() => {
    if (displayValue) return displayValue;
    const match = options.find((o) => o.value === value)?.label;
    if (match) return match;
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        N/A
      </Box>
    );
  })();

  return (
    <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
        {resolvedDisplay}
      </Box>
      {isEditable && (
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={`Edit ${ariaLabel}`}
          sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
        >
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
              width: 320,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Autocomplete<EditableSelectOption, false, false, false>
                size="small"
                disablePortal
                openOnFocus
                options={options}
                value={draft}
                getOptionLabel={(opt) => opt?.label ?? ''}
                getOptionDisabled={(opt) => !!opt.disabled}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                onChange={(_, next) => setDraft(next)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- opens on user intent
                    autoFocus
                    placeholder={placeholder}
                    inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                  />
                )}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCancel}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSave}
                  disabled={!draft}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    borderRadius: '4px',
                    bgcolor: 'v4.accent',
                    color: 'common.white',
                    cursor: draft ? 'pointer' : 'not-allowed',
                    opacity: draft ? 1 : 0.5,
                    fontFamily: 'inherit',
                    '&:hover': { opacity: draft ? 0.9 : 0.5 },
                    '&:disabled': { cursor: 'not-allowed', opacity: 0.5 },
                  }}
                >
                  Save
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
